// client/src/pages/Landing/Landing.jsx
import { useState, useRef, useEffect } from "react";
import GCU_LOPE from "./GCU_Lope_267.webp";
import "./Landing.css";
import axios from "axios";
import { DateTime } from "luxon";
import API_BASE_URL from "../../apiConfig";
import beepSuccessSound from "../../assets/beepSuccess.mp3";
import beepErrorSound from "../../assets/beepError.mp3";
import * as bootstrap from "bootstrap";

export default function Landing() {
  const [input, setInput] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const inputRef = useRef(null);
  const successAudio = useRef(null);
  const errorAudio = useRef(null);

  // Initial focus on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Initialize and refresh audio objects daily
  useEffect(() => {
    const initAudio = () => {
      successAudio.current = new Audio(beepSuccessSound);
      errorAudio.current = new Audio(beepErrorSound);
      successAudio.current.preload = "auto";
      errorAudio.current.preload = "auto";
    };
    initAudio();
    const refreshInterval = setInterval(initAudio, 24 * 60 * 60 * 1000); // Daily refresh
    return () => clearInterval(refreshInterval);
  }, []);

  // Periodic client reset every 48 hours
  useEffect(() => {
    const reloadInterval = setInterval(() => {
      console.log("Reloading to reset client state");
      window.location.reload();
    }, 48 * 60 * 60 * 1000); // Every 48 hours
    return () => clearInterval(reloadInterval);
  }, []);

  const handleInputChange = (e) => setInput(e.target.value);

  const handleClick = async (e) => {
    e.preventDefault();
    if (isSubmitting || !input.trim()) return;
    setIsSubmitting(true);

    const dt = DateTime.now();
    const dateNow = `${dt.month}-${dt.day}-${dt.year}`;
    const studentId = input.trim();
    const payload = {
      date: dateNow,
      Login: dt,
      Logout: "Still Signed In",
      StudentID: studentId,
    };

    try {
      console.log("Cookies before request:", document.cookie); // Debug cookies
      const response = await axios.post(
        `${API_BASE_URL}/signinTracker/${studentId}`,
        {},
        {
          timeout: 10000,
          withCredentials: true,
          validateStatus: (status) => status >= 200 && status < 500,
        }
      );

      console.log("Response:", response.status, response.data);

      if (response.data === "You are signed out!!") {
        showToast(
          `
          <div style="text-align: center;">
            You Signed out!!<br>
            Remember to give back your safety glasses<br>
            <img src="${process.env.PUBLIC_URL}/static/imgs/safe.png" alt="Safety Glasses" style="width:200px; height:auto; display: block; margin: 0 auto;"/>
          </div>
          `,
          "bg-info text-dark"
        );
        payload.Logout = dt;
        await axios.patch(`${API_BASE_URL}/logs/changeSignout/`, payload);
        await playSuccessSound();
      } else if (response.data === "You are not registered yet") {
        showToast("You are not registered yet", "bg-danger text-white");
        await playErrorSound();
      } else if (response.data?.name) {
        showToast(
          `Welcome Back ${response.data.name.split(" ")[0]}!`,
          "bg-success text-white"
        );

        const checkIfNewDay = await axios.post(`${API_BASE_URL}/logs/checkDate`, {
          date: dateNow,
        });
        if (checkIfNewDay.data === false) {
          await axios.post(`${API_BASE_URL}/logs/addToLog`, {
            date: dateNow,
            signIns: [],
          });
        }
        await axios.patch(`${API_BASE_URL}/logs/addSignIn`, payload);
        await playSuccessSound();
      } else {
        console.warn("Unexpected response:", response.data);
        showToast("Unexpected response", "bg-warning text-dark");
        await playErrorSound();
      }
    } catch (error) {
      console.error("API error:", error.message, error.response?.status);
      if (error.response?.status === 429) {
        showToast("Too many attempts. Please wait a moment.", "bg-danger text-white");
      } else {
        showToast("An error occurred. Please try again.", "bg-danger text-white");
      }
      await playErrorSound();
    } finally {
      setIsSubmitting(false);
      setInput("");
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const playSuccessSound = async () => {
    try {
      await successAudio.current.play();
    } catch (error) {
      console.error("Success sound failed:", error);
      successAudio.current = new Audio(beepSuccessSound);
      await successAudio.current.play();
    }
  };

  const playErrorSound = async () => {
    try {
      await errorAudio.current.play();
    } catch (error) {
      console.error("Error sound failed:", error);
      errorAudio.current = new Audio(beepErrorSound);
      await errorAudio.current.play();
    }
  };

  const showToast = (message, classes) => {
    const toastContainer = document.getElementById("toast-container");
    const toastElement = document.createElement("div");
    toastElement.className = `toast fade ${classes}`;
    toastElement.setAttribute("role", "alert");
    toastElement.setAttribute("aria-live", "assertive");
    toastElement.setAttribute("aria-atomic", "true");
    toastElement.innerHTML = `
      <div class="toast-body">
        ${message}
      </div>
    `;
    toastContainer.appendChild(toastElement);

    const toast = new bootstrap.Toast(toastElement, {
      autohide: true,
      delay: 3000,
    });
    toast.show();

    toastElement.addEventListener("hidden.bs.toast", () => {
      toastElement.remove();
    });
  };

  const backgroundStyle = {
    background: `url(${process.env.PUBLIC_URL}/static/imgs/outputWave.png) no-repeat center center ${
      isMobile ? "scroll" : "fixed"
    }`,
    backgroundSize: "cover",
  };

  return (
    <form
      className="landing"
      onSubmit={handleClick}
      title="The Great Wave off Kanagawa Print by Hokusai (1831) ASCII art"
      style={backgroundStyle}
    >
      <div className="centerPieces">
        <img className="customeLOPE" src={GCU_LOPE} alt="Lopelogo" />
        <h3 className="engineeringShop">Engineering Shop</h3>
        <input
          id="text"
          ref={inputRef}
          className="studentIdInput"
          autoComplete="off"
          type="text"
          placeholder="Student ID"
          value={input}
          onChange={handleInputChange}
          disabled={isSubmitting}
        />
      </div>
      <div className="circle" style={{ bottom: "-19%", right: "-11%", backgroundColor: "#562B99" }}></div>
      <div className="circle" style={{ bottom: "-33%", right: "4%", backgroundColor: "#562B99" }}></div>
      <div className="circle" style={{ bottom: "-19%", left: "-10%", backgroundColor: "#FBE500" }}></div>
      <div id="toast-container" className="toast-container"></div>
    </form>
  );
}